export interface ArticleBlock {
  type: "p" | "h2" | "quote" | "image";
  text?: string;
  src?: string;
  caption?: string;
}

export interface Article {
  id: string;
  title: string;
  summary: string;
  cover: string;
  category: string;
  categoryName: string;
  tags: string[];
  author: string;
  date: string;
  readTime: number;
  views: number;
  hot: boolean;
  featured: boolean;
  editorPick: boolean;
  topicId?: string;
  content: ArticleBlock[];
}

export const articles: Article[] = [
  {
    id: "harlem-renaissance",
    title: "从哈莱姆文艺复兴到民权运动：一个世纪的文化觉醒",
    summary:
      "二十世纪初的纽约哈莱姆，一场关于身份、艺术与尊严的自觉运动悄然兴起。它如何塑造了此后一个世纪黑人的文化表达？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20sepia%20abstract%20collage%20of%20early%20twentieth%20century%20harlem%20street%20architecture%20and%20art%20deco%20patterns%2C%20vintage%20textured%20paper%2C%20golden%20brown%20tones%2C%20archival%20editorial%20aesthetic%2C%20artistic%20composition&width=1200&height=800&seq=art-harlem-cover&orientation=landscape",
    category: "history",
    categoryName: "历史",
    tags: ["哈莱姆文艺复兴", "民权运动", "文化史", "身份认同"],
    author: "沈亦文",
    date: "2026-09-28",
    readTime: 12,
    views: 18420,
    hot: true,
    featured: true,
    editorPick: true,
    topicId: "civil-rights",
    content: [
      {
        type: "p",
        text: "1919 年的哈莱姆并不富裕，却异常拥挤而热烈。大量从南方迁来的黑人家庭挤在狭窄的街区里，带来了爵士乐、蓝调、教堂吟诵和口述传统。也正是在这里，一批作家、画家和音乐家开始意识到，他们不只是被描写的人，也可以成为书写者本身。",
      },
      { type: "h2", text: "一场关于“如何被看见”的运动" },
      {
        type: "p",
        text: "兰斯顿·休斯、佐拉·尼尔·赫斯顿、阿兰·洛克等人的作品，把黑人生活的日常质感带进了主流文学的视野。他们拒绝单一的苦难叙事，坚持书写复杂性——喜悦、幽默、矛盾与野心，同样值得被记录。",
      },
      {
        type: "quote",
        text: "我们不再满足于被他人讲述，我们要用自己的语言，讲述我们自己的故事。",
      },
      {
        type: "image",
        src: "https://readdy.ai/api/search-image?query=Warm%20abstract%20jazz%20club%20interior%20with%20golden%20stage%20light%20and%20vintage%20instruments%2C%20moody%20amber%20and%20brown%20tones%2C%20nostalgic%20atmosphere%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-harlem-inline&orientation=landscape",
        caption: "哈莱姆的爵士乐俱乐部成为文化觉醒的重要舞台",
      },
      { type: "h2", text: "从文化自觉到政治行动" },
      {
        type: "p",
        text: "这场运动没有止步于艺术。它为后来的民权运动提供了语言与底气：当一个群体能够自信地命名自己的经验，它也就获得了争取权利的正当性。文化觉醒与政治抗争，在后来的几十年里始终彼此交织。",
      },
      {
        type: "p",
        text: "近一个世纪过去，哈莱姆留下的问题依然尖锐：谁有权定义一种文化？谁决定什么值得被记住？这些问题，仍在被一代又一代人重新回答。",
      },
    ],
  },
  {
    id: "african-drums",
    title: "非洲鼓乐：跨越大陆的节奏记忆",
    summary:
      "鼓在许多非洲文化中不只是乐器，更是语言、政治与信仰的载体。当它被迫跨越大西洋，节奏也随之改变了世界音乐。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20African%20djembe%20drums%20silhouette%20with%20sunset%20gradient%20light%2C%20rich%20terracotta%20and%20orange%20tones%2C%20textured%20earthy%20background%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-drums-cover&orientation=landscape",
    category: "history",
    categoryName: "历史",
    tags: ["非洲鼓乐", "音乐史", "口述传统", "跨文化"],
    author: "柳承安",
    date: "2026-09-20",
    readTime: 9,
    views: 12310,
    hot: true,
    featured: false,
    editorPick: true,
    topicId: "jazz-century",
    content: [
      {
        type: "p",
        text: "在西非许多社群中，鼓手掌握着一种近似语言的能力。不同的鼓点对应不同的词句，能够传递消息、召唤仪式，甚至在集会上表达对权力的意见。鼓声从来不是背景，而是公共生活的一部分。",
      },
      { type: "h2", text: "一套被强制带走的语言" },
      {
        type: "p",
        text: "跨大西洋贸易把数百万人强行带离故土，也带走了他们的节奏。在许多地方，鼓被禁止使用，因为它被视为组织反抗的信号。然而节奏的记忆并未消失，它以拍手、跺脚、吟唱的形式存续下来。",
      },
      {
        type: "quote",
        text: "当乐器被夺走，身体就成了乐器；当语言被拆散，节奏就成了记忆。",
      },
      { type: "h2", text: "节奏如何重塑世界音乐" },
      {
        type: "p",
        text: "从新奥尔良的游行乐队，到古巴的伦巴、巴西的桑巴，再到后来的爵士与放克，非洲节奏的印记无处不在。理解这种流动，也就理解了现代流行音乐真正的根脉。",
      },
    ],
  },
  {
    id: "forgotten-inventors",
    title: "被遗忘的发明家：重塑现代生活的黑人创造者",
    summary:
      "从交通信号到家庭用品，许多塑造现代日常的发明背后，都有被历史淡化的黑人创作者。他们的故事值得被重新讲述。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20vintage%20workshop%20tools%20and%20technical%20blueprints%20on%20textured%20paper%2C%20aged%20brass%20and%20brown%20tones%2C%20nostalgic%20inventor%20aesthetic%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-inventors-cover&orientation=landscape",
    category: "history",
    categoryName: "历史",
    tags: ["发明史", "科技史", "人物", "再发现"],
    author: "陈砚清",
    date: "2026-09-12",
    readTime: 10,
    views: 9870,
    hot: false,
    featured: false,
    editorPick: true,
    content: [
      {
        type: "p",
        text: "历史教科书常常记住结果，却忽略过程，更忽略那些在边缘位置完成关键突破的人。在专利档案里，许多改变日常生活的发明来自黑人发明家，但他们的名字却长期缺席于公共记忆。",
      },
      { type: "h2", text: "日常背后的智慧" },
      {
        type: "p",
        text: "从改进的交通信号系统，到更耐用的鞋具、加热装置与制冷技术，这些看似普通的创造，实际上重新组织了他人的工作效率与生活品质。它们的价值，正在于“被使用”本身。",
      },
      {
        type: "quote",
        text: "真正的发明不只是灵光一闪，而是在被忽视处，持续解决具体的问题。",
      },
      { type: "h2", text: "为什么他们被遗忘" },
      {
        type: "p",
        text: "制度性的忽视、专利署名的不公，以及叙事权力的倾斜，共同造成了这种遗忘。重新讲述这些故事，并非为了简单弥补，而是为了让我们对“谁在推动进步”有更诚实的认识。",
      },
    ],
  },
  {
    id: "modern-literature",
    title: "黑人文学的现代谱系：从见证到想象",
    summary:
      "当代黑人写作早已超越单一的社会见证，进入更自由的想象领域。它如何在继承与突破之间找到自己的位置？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20cozy%20library%20abstract%20scene%20with%20stacked%20books%20and%20soft%20window%20light%2C%20rich%20brown%20and%20cream%20tones%2C%20literary%20nostalgic%20aesthetic%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-literature-cover&orientation=landscape",
    category: "history",
    categoryName: "历史",
    tags: ["文学", "小说", "写作", "当代"],
    author: "苏敏之",
    date: "2026-09-05",
    readTime: 11,
    views: 7620,
    hot: false,
    featured: false,
    editorPick: false,
    content: [
      {
        type: "p",
        text: "从二十世纪中叶到当下，黑人文学的形态发生了深刻变化。早期作品往往承担着“为群体发声”的重量，而今天，越来越多的作家选择自由地探索形式、幻想与内心世界。",
      },
      { type: "h2", text: "承载与挣脱" },
      {
        type: "p",
        text: "最优秀的写作从不回避历史，却也不被历史囚禁。它们把现实作为起点，然后飞向语言能够抵达的任何地方——魔幻、科幻、悬疑、回忆录，都成为可能的容器。",
      },
      {
        type: "quote",
        text: "写作不仅是对过去的交代，更是对尚未发生的事情的想象。",
      },
      {
        type: "p",
        text: "这种自由的扩展，本身就是一种进步：当一种文化不再需要不断解释自己的存在，它才真正获得了创造的权利。",
      },
    ],
  },
  {
    id: "jazz-century",
    title: "爵士乐的百年旅程：从新奥尔良到世界舞台",
    summary:
      "爵士乐诞生于街头的融合，却成为二十世纪最具国际影响力的音乐形式之一。它如何从地方声音走向全球语言？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20moody%20abstract%20jazz%20club%20stage%20with%20brass%20saxophone%20and%20amber%20spotlight%2C%20rich%20brown%20and%20gold%20tones%2C%20smoky%20nostalgic%20atmosphere%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-jazz-cover&orientation=landscape",
    category: "music",
    categoryName: "音乐",
    tags: ["爵士乐", "新奥尔良", "即兴", "音乐史"],
    author: "柳承安",
    date: "2026-10-02",
    readTime: 13,
    views: 21560,
    hot: true,
    featured: true,
    editorPick: true,
    topicId: "jazz-century",
    content: [
      {
        type: "p",
        text: "爵士乐没有单一的诞生时刻。它是游行铜管乐、蓝调、教堂吟诵与非裔节奏在新奥尔良的街巷里相遇、碰撞、发酵的结果。这种“混血”出身，也决定了它天生开放的性格。",
      },
      { type: "h2", text: "即兴：一种对话的艺术" },
      {
        type: "p",
        text: "爵士的核心并不在于记谱，而在于演奏者之间的实时对话。乐手在既定结构里不断做出选择，回应彼此，也回应现场。每一次演出都是不可复制的当下。",
      },
      {
        type: "quote",
        text: "在爵士里，错误不是终点，而是下一句旋律的起点。",
      },
      {
        type: "image",
        src: "https://readdy.ai/api/search-image?query=Warm%20abstract%20close%20up%20of%20vintage%20saxophone%20keys%20with%20golden%20reflections%2C%20rich%20brown%20and%20amber%20tones%2C%20soft%20studio%20lighting%2C%20artistic%20elegant%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-jazz-inline&orientation=landscape",
        caption: "即兴对话让每一次爵士演出都无法复制",
      },
      { type: "h2", text: "从地方到世界" },
      {
        type: "p",
        text: "随着唱片与广播的传播，爵士乐迅速走向世界，并不断被本土化。今天，从东京到巴黎，从开普敦到上海，乐手们用各自的方言继续这场百年对话。",
      },
    ],
  },
  {
    id: "soul-funk",
    title: "灵魂乐与放克：节奏里的社会宣言",
    summary:
      "六七十年代的灵魂乐与放克，不只是舞池的声音，更是一种集体自信的表达。它如何让身体成为政治的一部分？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20retro%20vinyl%20record%20and%20colorful%20stage%20lights%2C%20rich%20orange%20and%20brown%20tones%2C%20seventies%20groovy%20nostalgic%20aesthetic%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-soul-cover&orientation=landscape",
    category: "music",
    categoryName: "音乐",
    tags: ["灵魂乐", "放克", "节奏", "社会运动"],
    author: "何知远",
    date: "2026-09-25",
    readTime: 10,
    views: 15840,
    hot: true,
    featured: false,
    editorPick: false,
    topicId: "hiphop-culture",
    content: [
      {
        type: "p",
        text: "在民权运动的年代，音乐不只是陪伴，也是动员。灵魂乐把教堂的激情带进流行歌谣，放克则用密集的节奏建立起一种不可忽视的集体存在感。",
      },
      { type: "h2", text: "让身体发声" },
      {
        type: "p",
        text: "当一个人可以自由地舞动身体，他也在宣告对自己生活的某种主权。放克的低音与切分节奏，让舞池成为暂时摆脱束缚的空间，也让自信成为一种公共态度。",
      },
      {
        type: "quote",
        text: "节奏是最早的自由，也是最后被夺走的自由。",
      },
      { type: "h2", text: "持续的回响" },
      {
        type: "p",
        text: "从迪斯科到嘻哈，从电子乐到当代流行，灵魂乐与放克的基因被无数次采样与再创作。它证明：真正有力量的声音，从不真正消失。",
      },
    ],
  },
  {
    id: "hiphop-fifty",
    title: "嘻哈五十年：街头诗学的全球回响",
    summary:
      "从布朗克斯的街区派对到全球青年文化，嘻哈用半个世纪证明，最边缘的声音也能成为最响亮的语言。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20urban%20city%20skyline%20at%20dusk%20with%20graffiti%20texture%20and%20orange%20sunset%20light%2C%20rich%20brown%20and%20amber%20tones%2C%20urban%20nostalgic%20aesthetic%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-hiphop-cover&orientation=landscape",
    category: "music",
    categoryName: "音乐",
    tags: ["嘻哈", "说唱", "街头文化", "青年"],
    author: "何知远",
    date: "2026-10-08",
    readTime: 12,
    views: 26340,
    hot: true,
    featured: true,
    editorPick: true,
    topicId: "hiphop-culture",
    content: [
      {
        type: "p",
        text: "1973 年，布朗克斯的一场街区派对被许多人视为嘻哈的起点。DJ、MC、街舞与涂鸦，四种表达方式共同构成了这一文化最初的骨架。",
      },
      { type: "h2", text: "从派对到宣言" },
      {
        type: "p",
        text: "说唱起初是为派对助兴的语言游戏，很快却成为讲述处境、表达愤怒与野心的工具。它把街区的日常写进韵律，让被忽视的经验获得了公共的听众。",
      },
      {
        type: "quote",
        text: "一支麦克风，就能让沉默的街区被整个世界听见。",
      },
      {
        type: "image",
        src: "https://readdy.ai/api/search-image?query=Warm%20abstract%20close%20up%20of%20vintage%20turntable%20and%20vinyl%20records%20with%20orange%20glow%2C%20rich%20brown%20and%20amber%20tones%2C%20nostalgic%20street%20culture%20aesthetic%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-hiphop-inline&orientation=landscape",
        caption: "唱盘与采样，是嘻哈最早的语言工具",
      },
      { type: "h2", text: "成为全球语言" },
      {
        type: "p",
        text: "半个世纪后，嘻哈早已超越国界，成为世界各地年轻人讲述自身处境的方式。它的力量，正在于始终为“说出真相”保留空间。",
      },
    ],
  },
  {
    id: "black-cinema-wave",
    title: "黑人电影新浪潮：镜头背后的叙事革命",
    summary:
      "当越来越多黑人创作者拿起导筒，银幕上的形象开始摆脱刻板与单一。这场叙事革命带来了什么？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20film%20director%20chair%20and%20camera%20silhouette%20on%20set%2C%20golden%20hour%20orange%20light%2C%20rich%20brown%20tones%2C%20cinematic%20nostalgic%20aesthetic%2C%20artistic%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-cinema-cover&orientation=landscape",
    category: "film",
    categoryName: "影视",
    tags: ["电影", "导演", "叙事", "银幕形象"],
    author: "林望舒",
    date: "2026-09-30",
    readTime: 11,
    views: 13420,
    hot: true,
    featured: false,
    editorPick: true,
    content: [
      {
        type: "p",
        text: "很长一段时间里，黑人在银幕上只是被观看的对象——仆人、配角、危险符号。真正的转变，发生在他们开始掌控镜头之后。",
      },
      { type: "h2", text: "谁在讲述，谁被讲述" },
      {
        type: "p",
        text: "当创作者来自故事中的社区，叙事便有了不同的质地。幽默、亲密、矛盾与日常细节得以被保留，人物不再只是议题的载体，而是有血有肉的人。",
      },
      {
        type: "quote",
        text: "改变一部电影最彻底的方式，是改变谁坐在导演椅上。",
      },
      { type: "h2", text: "新的叙事可能" },
      {
        type: "p",
        text: "从独立电影到类型片，从纪录到科幻，新一代创作者正不断扩大可被讲述的题材。这不仅是行业进步，更是一种想象的解放。",
      },
    ],
  },
  {
    id: "oscars-path",
    title: "从边缘到主流：非裔演员的奥斯卡之路",
    summary:
      "奖项从来不只是荣誉，也是权力结构的映射。回看非裔演员的获奖历程，我们能看到怎样的变迁与局限？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20elegant%20stage%20curtain%20and%20golden%20trophy%20light%20bokeh%2C%20rich%20amber%20and%20brown%20tones%2C%20glamorous%20nostalgic%20aesthetic%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-oscars-cover&orientation=landscape",
    category: "film",
    categoryName: "影视",
    tags: ["奥斯卡", "表演", "行业", "认可"],
    author: "林望舒",
    date: "2026-09-16",
    readTime: 9,
    views: 8920,
    hot: false,
    featured: false,
    editorPick: false,
    content: [
      {
        type: "p",
        text: "聚光灯下的荣耀，往往掩盖了漫长而艰难的争取。非裔演员进入主流奖项视野的过程，本身就是一部关于承认与反抗的历史。",
      },
      { type: "h2", text: "被承认的代价" },
      {
        type: "p",
        text: "早期的获奖往往附加条件——角色需要符合主流期待，故事需要让多数观众感到舒适。真正自由的表演，需要等到行业结构本身发生松动。",
      },
      {
        type: "quote",
        text: "奖项追认的从不是天赋，而是权力愿意承认什么。",
      },
      {
        type: "p",
        text: "今天，讨论已从“能否获奖”转向“如何被公平地讲述”。这个问题的转变，本身就是进步的信号。",
      },
    ],
  },
  {
    id: "contemporary-artists",
    title: "当代非裔艺术家的视觉语言",
    summary:
      "在拍卖行与美术馆之外，当代非裔艺术家正用绘画与装置重新定义身份的表达方式。他们在回应什么？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20modern%20art%20gallery%20interior%20with%20colorful%20abstract%20paintings%20on%20walls%2C%20soft%20warm%20lighting%2C%20rich%20brown%20and%20orange%20tones%2C%20minimal%20elegant%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-contemporary-cover&orientation=landscape",
    category: "art",
    categoryName: "艺术",
    tags: ["当代艺术", "绘画", "装置", "身份"],
    author: "程未晚",
    date: "2026-10-05",
    readTime: 12,
    views: 11230,
    hot: true,
    featured: true,
    editorPick: true,
    topicId: "black-art-power",
    content: [
      {
        type: "p",
        text: "今天的非裔艺术家面对的问题，与前几代已经不同。他们不再只需要证明“我们也能创作”，而是可以自由地质问、拆解与重构，甚至拒绝被身份定义。",
      },
      { type: "h2", text: "材料也是立场" },
      {
        type: "p",
        text: "头发、布料、旧照片、日常物件……许多创作者刻意使用与生活经验相关的材料，让作品从抽象概念落回具体的身体与记忆。",
      },
      {
        type: "quote",
        text: "当材料来自生活本身，作品就不再只是被观看，而是被辨认。",
      },
      {
        type: "image",
        src: "https://readdy.ai/api/search-image?query=Warm%20abstract%20close%20up%20of%20textured%20mixed%20media%20artwork%20with%20fabric%20and%20paint%2C%20rich%20earthy%20tones%20with%20orange%20accents%2C%20soft%20gallery%20lighting%2C%20artistic%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-contemporary-inline&orientation=landscape",
        caption: "综合材料让身份表达回到具体的触感",
      },
      { type: "h2", text: "在市场中保持清醒" },
      {
        type: "p",
        text: "随着艺术市场对非裔艺术家关注度上升，新的张力也随之出现：如何在被追捧的同时，不被符号化。这成为当代创作必须面对的课题。",
      },
    ],
  },
  {
    id: "textile-identity",
    title: "拼贴、纺织与身份：手工艺中的文化密码",
    summary:
      "在拼布、编织与纺织中，隐藏着一套被长期低估的知识体系。手工艺如何成为讲述历史的另一种方式？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20colorful%20patchwork%20quilt%20texture%20close%20up%20with%20geometric%20patterns%2C%20rich%20earthy%20orange%20and%20brown%20tones%2C%20cozy%20handmade%20aesthetic%2C%20artistic%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-textile-cover&orientation=landscape",
    category: "art",
    categoryName: "艺术",
    tags: ["手工艺", "纺织", "拼布", "民间艺术"],
    author: "程未晚",
    date: "2026-09-18",
    readTime: 8,
    views: 6410,
    hot: false,
    featured: false,
    editorPick: true,
    topicId: "black-art-power",
    content: [
      {
        type: "p",
        text: "传统艺术史长期把绘画与雕塑置于中心，而把纺织、拼布、编织归入“工艺”的边缘。但正是这些被低估的实践中，保存着最密集的记忆与社群知识。",
      },
      { type: "h2", text: "被缝进布里的历史" },
      {
        type: "p",
        text: "许多拼布作品由多人共同完成，图案、色彩与缝线都承载着制作者的生活故事。它们既是实用的物件，也是家庭历史的档案。",
      },
      {
        type: "quote",
        text: "一针一线之间，藏着比文字更顽强的传承。",
      },
      {
        type: "p",
        text: "今天，越来越多当代艺术家重新拾起这些技法，让手工艺从被低估的边缘，走向美术馆的正中央。",
      },
    ],
  },
  {
    id: "dance-body-politics",
    title: "舞蹈与身体政治：从林迪舞到街舞",
    summary:
      "舞蹈从来不只是娱乐。当身体自由地移动，它也在挑战规则、协商空间，并书写属于自己的叙事。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20dynamic%20dancer%20silhouette%20in%20motion%20with%20orange%20stage%20light%2C%20rich%20brown%20and%20amber%20tones%2C%20energetic%20artistic%20atmosphere%2C%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-dance-cover&orientation=landscape",
    category: "art",
    categoryName: "艺术",
    tags: ["舞蹈", "街舞", "身体", "空间"],
    author: "苏敏之",
    date: "2026-09-08",
    readTime: 9,
    views: 7280,
    hot: false,
    featured: false,
    editorPick: false,
    content: [
      {
        type: "p",
        text: "在充满限制的环境中，身体如何移动，本身就是一种表达。从社交舞厅到街头，黑人舞蹈始终在定义“自由”的具体形状。",
      },
      { type: "h2", text: "空间与可能性" },
      {
        type: "p",
        text: "当公共场所对某些身体并不友好时，舞蹈成为重新占据空间的方式。一次旋转、一次对峙式的即兴，都是在宣告：这里也有我的位置。",
      },
      {
        type: "quote",
        text: "身体一旦开始自由地移动，规则就开始被重新协商。",
      },
      {
        type: "p",
        text: "今天，街舞已成为全球性的语言。它的每一次流行，都在提醒我们：文化的流动，往往始于对自由的渴望。",
      },
    ],
  },
  {
    id: "education-landscape",
    title: "当代黑人社区的教育图景",
    summary:
      "教育资源的不均衡，塑造了不同社区的成长轨迹。走进当代黑人社区的教育现场，我们能看到挑战与希望并存的现实。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20sunlit%20classroom%20with%20books%20and%20chalkboard%2C%20golden%20natural%20light%2C%20rich%20brown%20and%20cream%20tones%2C%20hopeful%20nostalgic%20aesthetic%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-education-cover&orientation=landscape",
    category: "society",
    categoryName: "社会动态",
    tags: ["教育", "社区", "公平", "成长"],
    author: "沈亦文",
    date: "2026-10-10",
    readTime: 10,
    views: 10450,
    hot: true,
    featured: false,
    editorPick: false,
    content: [
      {
        type: "p",
        text: "教育被普遍视为改变命运的通道，但通道本身并不总是平等地向所有人敞开。资源分配、师资与期待，共同塑造着不同社区的成长经验。",
      },
      { type: "h2", text: "期待的力量" },
      {
        type: "p",
        text: "研究表明，教育者对学生的期待会显著影响学习结果。当制度性的低期待被打破，被压抑的潜力往往会被迅速释放。",
      },
      {
        type: "quote",
        text: "公平不是给所有人相同的东西，而是让每个人都能真正开始。",
      },
      { type: "h2", text: "在社区里生长" },
      {
        type: "p",
        text: "许多由社区自发组织的辅导、艺术与阅读项目，正在填补公共资源的缝隙。它们规模不大，却持续产生着真实的影响。",
      },
    ],
  },
  {
    id: "language-identity",
    title: "语言与身份：非裔英语文化的演变",
    summary:
      "语言既是沟通工具，也是身份的标志。非裔英语如何在被贬低与自我认同之间，发展出独特而丰富的表达体系？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20vintage%20typewriter%20and%20handwritten%20letters%20on%20desk%2C%20rich%20brown%20and%20cream%20tones%2C%20soft%20window%20light%2C%20nostalgic%20literary%20aesthetic%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-language-cover&orientation=landscape",
    category: "society",
    categoryName: "社会动态",
    tags: ["语言", "身份", "方言", "文化"],
    author: "陈砚清",
    date: "2026-09-22",
    readTime: 8,
    views: 5980,
    hot: false,
    featured: false,
    editorPick: false,
    content: [
      {
        type: "p",
        text: "语言从来不是中性的。它承载着使用者的历史、处境与创造力。非裔英语的形成，本身就是一部跨越数代的适应与创造史。",
      },
      { type: "h2", text: "被误解的表达" },
      {
        type: "p",
        text: "长期以来，这种语言被主流视为“不标准”。然而语言学研究表明，它拥有完整而严谨的语法系统，是一种有序、富有生命力的语言变体。",
      },
      {
        type: "quote",
        text: "当一种说法被贬为错误，往往是说它的人先被贬低。",
      },
      {
        type: "p",
        text: "如今，这种语言的影响早已渗入流行音乐、文学与网络文化。承认它的价值，也是承认使用者的尊严。",
      },
    ],
  },
  {
    id: "soul-food-migration",
    title: "南方风味与迁徙记忆：黑人饮食文化的旅程",
    summary:
      "一道菜的背后，可能是一段被迫迁徙的旅程。南方风味如何在迁徙中被重塑，又如何在今天成为文化象征？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20cozy%20home%20cooked%20meal%20on%20rustic%20wooden%20table%20with%20rich%20colorful%20dishes%2C%20golden%20natural%20light%2C%20rich%20brown%20and%20orange%20tones%2C%20homely%20editorial%20food%20photography%2C%20artistic%20composition&width=1200&height=800&seq=art-food-cover&orientation=landscape",
    category: "society",
    categoryName: "社会动态",
    tags: ["饮食文化", "迁徙", "南方", "记忆"],
    author: "何知远",
    date: "2026-09-11",
    readTime: 9,
    views: 8150,
    hot: false,
    featured: false,
    editorPick: true,
    content: [
      {
        type: "p",
        text: "食物是记忆最直接的载体。当大量黑人家庭从南方迁往北方与西部城市，他们带走的不仅是行李，还有一整套关于烹饪的智慧与情感。",
      },
      { type: "h2", text: "在有限中创造丰盛" },
      {
        type: "p",
        text: "许多经典菜肴诞生于原料有限的处境，却通过巧妙的搭配与慢炖的耐心，创造出令人难忘的风味。这本身就是一种极具韧性的创造力。",
      },
      {
        type: "quote",
        text: "把有限的食材做成丰盛的一餐，是一种被低估的智慧。",
      },
      { type: "h2", text: "从家庭餐桌到文化符号" },
      {
        type: "p",
        text: "今天，这些菜肴既是家庭团聚的核心，也是被更广泛人群认识与喜爱的文化象征。理解它们的来历，也是理解一段迁徙的历史。",
      },
    ],
  },
  {
    id: "gospel-music",
    title: "福音音乐：从教堂吟诵到全球舞台",
    summary:
      "福音音乐诞生于信仰的表达，却深刻塑造了灵魂乐、节奏布鲁斯乃至流行音乐。它的力量究竟从何而来？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20sunlit%20church%20interior%20with%20wooden%20pews%20and%20soft%20stained%20glass%20glow%2C%20golden%20amber%20and%20brown%20tones%2C%20spiritual%20serene%20atmosphere%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-gospel-cover&orientation=landscape",
    category: "music",
    categoryName: "音乐",
    tags: ["福音音乐", "信仰", "合唱", "音乐史"],
    author: "柳承安",
    date: "2026-09-14",
    readTime: 9,
    views: 9040,
    hot: false,
    featured: false,
    editorPick: false,
    content: [
      {
        type: "p",
        text: "在教堂里，歌声从来不只是仪式的一部分，更是共同体表达希望、悲伤与坚韧的方式。福音音乐正是这种集体情感最直接的载体。",
      },
      { type: "h2", text: "会众即合唱" },
      {
        type: "p",
        text: "与专业演出不同，福音音乐的核心在于参与。会众的回应、领唱与合唱的呼应，让每一次演唱都成为一次共同的呼吸。",
      },
      {
        type: "quote",
        text: "当所有人一起歌唱，苦难就不再只是个人的重量。",
      },
      { type: "h2", text: "走出教堂之后" },
      {
        type: "p",
        text: "二十世纪中叶，福音音乐的表达方式进入流行乐坛，深刻影响了灵魂乐与节奏布鲁斯。它的热情与真诚，成为无数歌手共享的语言。",
      },
    ],
  },
  {
    id: "urban-community",
    title: "社区空间的争夺：城市更新中的日常",
    summary:
      "当老街区被重新开发，居民的生活如何被改变？城市更新背后，是谁的声音被放大，又是谁在沉默中搬迁？",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20abstract%20city%20street%20with%20old%20brick%20buildings%20and%20warm%20evening%20light%2C%20rich%20brown%20and%20orange%20tones%2C%20urban%20nostalgic%20aesthetic%2C%20artistic%20minimal%20composition%2C%20editorial%20photography&width=1200&height=800&seq=art-urban-cover&orientation=landscape",
    category: "society",
    categoryName: "社会动态",
    tags: ["城市更新", "社区", "空间", "公共议题"],
    author: "何知远",
    date: "2026-09-03",
    readTime: 10,
    views: 7360,
    hot: false,
    featured: false,
    editorPick: false,
    content: [
      {
        type: "p",
        text: "城市的改变总是以“发展”为名，但改变的成本，往往由最没有议价能力的人承担。当租金上涨、店铺更替，一个社区的肌理也随之被改写。",
      },
      { type: "h2", text: "谁的空间，谁的记忆" },
      {
        type: "p",
        text: "对居民而言，街区不只是地址，更是关系、记忆与身份的载体。空间的重组，常常意味着这些无形资产的流失。",
      },
      {
        type: "quote",
        text: "一个社区真正的价值，在于人与人之间那些无法被估价的关系。",
      },
      { type: "h2", text: "在变化中争取主动" },
      {
        type: "p",
        text: "面对不可逆的变化，越来越多的社区开始组织起来，通过参与式规划与公共讨论，为自己的生活方式争取一席之地。",
      },
    ],
  },
];

export const articleMap: Record<string, Article> = articles.reduce((acc, item) => {
  acc[item.id] = item;
  return acc;
}, {} as Record<string, Article>);